import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  FormGroup,
  Col,
  Row,
  Container,
  Table,
} from "reactstrap";
import "./index.css";
import ApiCaller from "api/apiCaller";
import Loading from "components/Loading";
import api from "api";
import { UserInfo } from "model/user";
import { ApiResponse } from "model/response";
import {
  Dataset,
  DatasetDetailResponse,
} from "model/dataset";
import _ from "lodash";
import ReactPaginate from "react-paginate";
import {
  TableListingResponse,
  Table as TableType,
} from "model/table";
import { useRouteMatch } from "react-router-dom";
import EditbaleInput from "components/EditableInput";

const PAGE_LIMIT = 50;
interface ListingData {
  tables: Array<TableType>;
  page: number;
  total: number;
}

export default function DatasetDetail() {
  const match = useRouteMatch("/dataset/:dataset_id");
  const params: any = match?.params;
  const datasetId = params?.dataset_id;

  const companyId: any = api.getCurrentCompanyId();
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [listingData, setListingData] = useState<ListingData>({
    tables: [],
    page: 0,
    total: 0,
  });

  const canEditTable= currentUser?.is_admin || currentUser?.is_company_admin || currentUser?.is_data_practitioner;
  const canViewChangelog= currentUser?.is_admin || currentUser?.is_company_admin;

  useEffect(() => {
    getDatasetDetail();
  }, []);

  useEffect(() => {
    fetchData();
  }, [listingData?.page]);

  const initData = async () => {
    const user = api.getCurrentUser();
    setCurrentUser(user);
  };

  useEffect(() => {
    initData();
  }, []);

  const fetchData = async () => {
    let url = `table?company_id=${companyId}&dataset_id=${datasetId}&limit=${PAGE_LIMIT}&page=${listingData?.page}`;
    if (searchKeyword !== "") {
      url = url + `&keyword=${searchKeyword}`;
    }
    const res: ApiResponse = await ApiCaller.get(url);
    if (res?.status === "ok") {
      const data: TableListingResponse = res?.data;
      setListingData({
        ...listingData,
        ...{ tables: data?.tables, total: data?.total },
      });
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const getDatasetDetail = async () => {
    const url = `dataset/${datasetId}`;
    const res: ApiResponse = await ApiCaller.get(url);
    if (res?.status === "ok") {
      const data: DatasetDetailResponse = res?.data;
      setDataset(data.dataset);
    }
  };

  if (loading) return <Loading />;

  return (
    <Container className="dataset__detail">
      <h2>{dataset?.name}</h2>
      <Table responsive className="dataset__info">
        <thead>
          <tr><th>Dataset info</th></tr>
        </thead>
        <tbody>
          <tr key='dataset_project'>
            <td className="dataset__info_attribute_label">Project ID</td>
            <td>{dataset?.project_id}</td>
          </tr>
          <tr key='dataset_creation_time'>
            <td className="dataset__info_attribute_label">Created at</td>
            <td>{dataset?.creation_time}</td>
          </tr>
          <tr key='dataset_last_modified'>
            <td className="dataset__info_attribute_label">Last modified</td>
            <td>{dataset?.last_modified}</td>
          </tr>
          <tr key='dataset_data_location'>
            <td className="dataset__info_attribute_label">Data location</td>
            <td>{dataset?.location}</td>
          </tr>
          <tr key='dataset_description'>
            <td className="dataset__info_attribute_label">Description</td>
            <td className="custom-textarea-multi-line">{dataset?.description}</td>
          </tr>
          <tr key='dataset_label'>
            <td className="dataset__info_attribute_label">Labels</td>
            <td>
              {dataset?.labels &&
                Object.keys(dataset?.labels).map(key => (
                  <span key={key} className="item__label">{key}:<b>{dataset?.labels[key]}</b><br/></span>
                ))
              }
            </td>
          </tr>
        </tbody>
      </Table>
      <Row>
        <Col xs={12}>
          <FormGroup>
            <Input
              onKeyPress={(e: any) => {
                if (e.code == "Enter") {
                  fetchData();
                }
              }}
              placeholder="Search by name, code name, description... of table"
              onChange={(e) => {
                setSearchKeyword(e.target.value);
                setListingData({
                  ...listingData,
                  ...{ page: 0 },
                });
              }}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Button
            color="primary"
            onClick={() => {
              fetchData();
            }}
          >
            Search
          </Button>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <ReactPaginate
            previousLabel={"<"}
            nextLabel={">"}
            breakLabel={"..."}
            breakClassName={"break-me"}
            pageCount={Math.floor(listingData?.total / PAGE_LIMIT) + 1}
            marginPagesDisplayed={3}
            pageRangeDisplayed={2}
            initialPage={0}
            onPageChange={async (page) => {
              setListingData({
                ...listingData,
                ...{ page: page.selected },
              });
            }}
            containerClassName={"pagination"}
            activeClassName={"active"}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <br />
          <Table responsive striped>
            <thead>
              <tr>
                <th>Table Name</th>
                <th>Code Name</th>
                <th>Description</th>
                <th>Last Edit By</th>
                {canViewChangelog && <th></th>}
              </tr>
            </thead>
            <tbody>
              {listingData.tables.map((table) => (
                <tr key={`${table.id}`}>
                  <td>
                    <a href={`/table/${table.id}`}>
                      <span>{table?.name}</span>
                    </a>
                    <br/>
                    {table?.labels &&
                      Object.keys(table?.labels).map(key => (
                        <span key={key} className="item__label">{key}:<b>{table?.labels[key]}</b><br/></span>
                      ))
                    }
                  </td>
                  <td>{table.code_name}</td>
                  <td className='custom-textarea-multi-line'>
                  {canEditTable ?
                    <EditbaleInput
                      index={"table"}
                      id={table.id}
                      updateKey={"description"}
                      updateValue={table?.description}
                    /> : <span>{table.description}</span>
                  }
                  </td>
                  <td>{table.last_edit_by_email}</td>
                  {canViewChangelog &&
                    <td>
                      <a href={`/dictionary/changelog/table/${table.id}`}>
                        changelog
                      </a>
                    </td>
                  }
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}
